import React, { useEffect, useState } from 'react';
import { differenceInBusinessDays, format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import type { Employee } from '../../types/hr';
import { displayName } from '../../utils/employees';
import { TODAY } from '../../utils/format';
import { hrActions } from '../../utils/hrStore';
import { balanceFor, policyName } from '../../utils/timeOff';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
interface RequestTimeOffModalProps {
  open: boolean;
  employee: Employee;
  onClose: () => void;
}
export function RequestTimeOffModal({
  open,
  employee,
  onClose
}: RequestTimeOffModalProps) {
  const [start, setStart] = useState('2026-11-16');
  const [end, setEnd] = useState('2026-11-17');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setStart('2026-11-16');
      setEnd('2026-11-17');
      setNote('');
    }
  }, [open]);
  const valid = Boolean(start && end && end >= start);
  const days = valid ? Math.max(1, differenceInBusinessDays(parseISO(end), parseISO(start)) + 1) : 0;
  const available = balanceFor(employee.id)?.available ?? 0;
  const after = available - days;
  const submit = () => {
    if (!valid || !employee.policyId) return;
    hrActions.addRequest({
      id: `r-${Date.now()}`,
      employeeId: employee.id,
      start,
      end,
      days,
      policyId: employee.policyId,
      note: note.trim() || 'Submitted by HR on behalf of the employee.',
      submitted: format(TODAY, 'yyyy-MM-dd'),
      balanceBefore: available,
      status: 'pending'
    });
    toast.success('Time off request created', {
      description: `${days} day${days === 1 ? '' : 's'} for ${displayName(employee)} — added to the Requests queue.`
    });
    onClose();
  };
  const inputClass = 'mt-1.5 h-11 w-full rounded-xl border-0 bg-white px-3.5 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure';
  return <Modal open={open} onClose={onClose} title="Request time off" description={`On behalf of ${displayName(employee)}`} footer={<>
          <Button onClick={onClose} data-id="element-423" data-mp="2zp620h4ylpf3">Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!valid || !employee.policyId} data-id="element-424" data-mp="2vovwf5l2x10r">
            Submit request
          </Button>
        </>} data-id="element-422" data-mp="34qd7uanjkej4">
      {employee.policyId ? <div className="space-y-4" data-id="element-425" data-mp="2ls0wqjd2kukw">
          <div className="rounded-xl bg-canvas px-4 py-3 text-sm" data-id="element-426" data-mp="2lt2awwpdtx6u">
            <span className="text-muted" data-id="element-427" data-mp="2h0iwjxivey0z">Policy</span> <span className="font-medium text-navy" data-id="element-428" data-mp="2gzg5npljsvl2">{policyName(employee.policyId)}</span>
            <span className="mx-2 text-slate-300" data-id="element-429" data-mp="2c1xxi1fcuke4">|</span>
            <span className="text-muted" data-id="element-430" data-mp="280me4dn2qved">Available</span> <span className="font-medium text-navy" data-id="element-431" data-mp="281xiaekfm560">{available} days</span>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-432" data-mp="230gmuykpvawt">
            <label className="text-xs font-medium text-slate-600" data-id="element-433" data-mp="1y26l254hlc28">
              Start date
              <input type="date" value={start} onChange={e => setStart(e.target.value)} className={inputClass} data-id="element-434" data-mp="dk4iofi8jbh0" />
            </label>
            <label className="text-xs font-medium text-slate-600" data-id="element-435" data-mp="ihw7h0nl2hgz">
              End date
              <input type="date" value={end} min={start} onChange={e => setEnd(e.target.value)} className={inputClass} data-id="element-436" data-mp="nj3ir9oxpyse" />
            </label>
          </div>
          <label className="block text-xs font-medium text-slate-600" data-id="element-437" data-mp="rhawrws0klzw">
            Note (optional)
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Add context for the approver…" className="mt-1.5 w-full rounded-xl border-0 bg-white px-3.5 py-2.5 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-438" data-mp="3qwxxgj36clem" />
          </label>
          <div className="flex items-center justify-between rounded-xl px-4 py-3 ring-1 ring-inset ring-slate-100" data-id="element-439" data-mp="3c9cq2cz9o00w">
            <span className="text-sm text-muted" data-id="element-440" data-mp="2t6axi52zxy48">{valid ? `${days} business day${days === 1 ? '' : 's'}` : 'End date must be after start'}</span>
            <span className={`text-sm font-semibold ${after < 0 ? 'text-rose-600' : 'text-navy'}`} data-id="element-441" data-mp="2o4bu4v79b17a">{after} days after</span>
          </div>
        </div> : <p className="text-sm text-muted" data-id="element-442" data-mp="2y3knbluasbsl">
          {displayName(employee)} isn't assigned to a time off policy. Regular PTO currently covers the Operations pilot group; new hires are on New Hire PTO.
        </p>}
    </Modal>;
}